"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { setSiteTitle } from "@/lib/page-titles";
import { LandingFooter } from "@/components/ui/footer";
import { ScrollRevealText } from "@/components/ui/scroll-reveal-text";
import { ContentLockup } from "@/components/ui/content-lockup";
import { isLoginEnabled } from "@/lib/utils";
import { SiteNavigation } from "@/components/ui/site-navigation";
import { PillButton } from "@/components/ui/pill-button";
import { env } from "@/env";
import dynamic from "next/dynamic";

// Dynamically import LatticeBackground to avoid SSR issues with Three.js
const LatticeBackground = dynamic(
  () => import("@/components/parametric/LatticeBackground"),
  { ssr: false }
);

// Dynamically import Sheet to avoid SSR issues with Three.js
const Sheet = dynamic(
  () => import("@/components/parametric/Sheet").then((mod) => mod.Sheet),
  { ssr: false }
);

// Dynamically import BloomingNetworkHelix to avoid SSR issues with Three.js
const BloomingNetworkHelix = dynamic(
  () =>
    import("@/components/parametric/BloomingNetworkHelix").then(
      (mod) => mod.BloomingNetworkHelix
    ),
  { ssr: false }
);

// Dynamically import DotsOnLines to avoid SSR issues with Three.js
const DotsOnLines = dynamic(
  () =>
    import("@/components/parametric/DotsOnLines").then(
      (mod) => mod.DotsOnLines
    ),
  { ssr: false }
);

// Dynamically import BloomingNetworkComplex to avoid SSR issues with Three.js
const BloomingNetworkComplex = dynamic(
  () =>
    import("@/components/parametric/BloomingNetworkComplex").then(
      (mod) => mod.BloomingNetworkComplex
    ),
  { ssr: false }
);

// Dynamically import CommutativeSymbolsNetwork to avoid SSR issues with Three.js
const CommutativeSymbolsNetwork = dynamic(
  () =>
    import("@/components/parametric/CommutativeSymbolsNetwork").then(
      (mod) => mod.CommutativeSymbolsNetwork
    ),
  { ssr: false }
);

// Dynamically import CommutativeNetwork to avoid SSR issues with Three.js
const CommutativeNetwork = dynamic(
  () =>
    import("@/components/parametric/CommutativeNetwork").then(
      (mod) => mod.CommutativeNetwork
    ),
  { ssr: false }
);

// Dynamically import ArcsLinesDots to avoid SSR issues with Three.js
const ArcsLinesDots = dynamic(
  () =>
    import("@/components/parametric/ArcsLinesDots").then(
      (mod) => mod.ArcsLinesDots
    ),
  { ssr: false }
);

export default function HomePage() {
  useEffect(() => {
    setSiteTitle();
  }, []);

  return (
    <main className="relative min-h-screen bg-[#FFFDF0] text-[#0a0a0a] overflow-x-hidden">
      <div className="pointer-events-none absolute right-0 top-0 h-[912px] left-0 overflow-hidden opacity-60">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/images/landing/hero-background.svg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* Gradient overlay to fade out at top */}
        <div
          className="absolute top-0 left-0 right-0 h-[450px] z-10 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(255, 253, 240, 1) 0%, rgba(255, 253, 240, 0) 100%)",
          }}
        />
      </div>
      <SiteNavigation isDark={false} />
      <div className="relative mx-auto max-w-[96rem]">
        {/* Parametric animation background */}
        <NewHero />
      </div>
      <BeyondPatternMatchingSection />
      <OurThesisSection />
      <AgenticaSection />
      <OurTeamSection />
      <LandingFooter
        colorMode="gray"
        className="max-w-[96rem] lg:max-w-[96rem] xl:max-w-[96rem] px-8 sm:px-8 lg:px-24 xl:px-24"
      />
    </main>
  );
}

function NewHero() {
  return (
    <>
      {/* Mobile layout: asset on top, divider, then text */}
      <div className="md:hidden flex flex-col -mt-36 pt-[121px]">
        {/* Animation area — the SheetBackground behind shows through */}
        <div className="relative h-[280px]" />
        {/* Horizontal divider */}
        <div className="w-full h-px bg-black/20" />
        {/* Text content — blurred background for legibility */}
        <div className="px-6 py-10 relative">
          <div className="absolute inset-0 backdrop-blur-sm bg-[#FFFDF0]/40" />
          <div className="relative z-10">
            <ContentLockup
              label="About Us"
              title="Intelligence, Redefined"
              subhead="Symbolica is an artificial intelligence research lab that is pioneering the application of category theory and type theory to build a symbolic reasoning engine."
              description="We have launched Agentica, an agent-builder that uses our open-source Agentica SDK. Try it now."
              light
            />
            <div className="mt-8 flex flex-row flex-wrap gap-4">
              <Link href="/research">
                <PillButton variant="outline" icon="none">
                  Our Approach
                </PillButton>
              </Link>
              <a href={env.NEXT_PUBLIC_AGENTICA_BASE_URL}>
                <PillButton variant="outline">
                  <span className="hidden min-[440px]:inline">Try </span>
                  Agentica
                </PillButton>
              </a>
            </div>
          </div>
        </div>
        {/* Bottom divider */}
        <div className="w-full h-px bg-black/20" />
      </div>

      {/* Desktop layout: side-by-side grid */}
      <div className="hidden md:grid h-[calc(100vh-80px)] min-h-[780px] lg:min-h-[700px] max-h-[1060px] relative w-full grid-cols-[60%_40%] lg:grid-cols-[55%_45%] grid-rows-[25%_75%] -mt-30 lg:-mt-20">
        {/* TL */}
        <div className="relative flex flex-col justify-end gap-8 p-8">
          {/* R Border */}
          <div className="absolute bottom-0 right-0 top-16 w-px bg-gradient-to-b from-black/0 to-black/20" />
        </div>

        {/* TR */}
        <div className="col-start-2 row-start-1 relative" />

        <div
          className="relative flex flex-col items-start px-24 pt-20 pb-32"
          style={{ paddingTop: "calc(5rem + 60px)" }}
        >
          {/* Opaque BG — extends up to header */}
          <div className="-left-1000 absolute bottom-0 -top-full right-0 bg-[#FFFDF0]" />
          {/* Content */}
          <div className="flex flex-col items-start z-10">
            <ContentLockup
              label="About Us"
              title="Intelligence, Redefined"
              subhead="Symbolica is an artificial intelligence research lab that is pioneering the application of category theory and type theory to build a symbolic reasoning engine."
              description="We have launched Agentica, an agent-builder that uses our open-source Agentica SDK. Try it now."
              light
            />
            {/* Btns */}
            <div className="mb-8 mt-8 lg:mt-10 lg:mb-16 flex flex-row flex-wrap gap-4">
              <Link href="/research">
                <PillButton variant="outline" icon="none">
                  Our Approach
                </PillButton>
              </Link>
              <a href={env.NEXT_PUBLIC_AGENTICA_BASE_URL}>
                <PillButton variant="outline">Try Agentica</PillButton>
              </a>
            </div>
          </div>
          {/* R Border */}
          <div className="absolute bottom-0 right-0 top-0 w-px bg-black/20" />
          {/* B Border */}
          <div className="absolute -left-10000 right-0 bottom-0 h-px bg-black/20" />
        </div>

        {/* BR */}
        <div className="relative z-10 col-start-2 row-start-2">
          {/* B Border */}
          <div className="absolute bottom-0 left-0 w-10000 h-px bg-black/20" />
        </div>
      </div>
    </>
  );
}

function BeyondPatternMatchingSection() {
  const firstParagraph = `Current state-of-the-art models do not directly incorporate code execution as a first class primitive at the architectural level. In order to build a general purpose reasoning engine we must combine symbolic and neural intelligence within the architecture itself. We believe this is the most important outstanding problem in artificial intelligence research. Several fundamental breakthroughs are required to achieve this goal.`;

  // Track if section is in viewport to lazy load visualization
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection observer to mount/unmount visualization based on viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry?.isIntersecting ?? false);
      },
      { rootMargin: "100px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="discover"
      className="relative bg-[#FFFDF0] border-b border-black/20 scroll-mt-20 overflow-hidden"
    >
      {/* Original content area */}
      <div className="mx-auto max-w-[96rem] px-8 lg:px-24 pt-32 md:pt-48 pb-10 md:pb-10">
        <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-black/60 mb-10">
          BEYOND PATTERN MATCHING
        </div>
        <ScrollRevealText paragraphs={[firstParagraph]} className="max-w-4xl" />
      </div>

      {/* Sheet visualization at bottom - the new 0.5x height */}
      <div className="relative h-[150px] md:h-[200px]">
        {/* The visualization */}
        <div
          className={`absolute inset-0 transition-opacity duration-700 delay-1000 ${isInView ? "opacity-100" : "opacity-0"}`}
        >
          {isInView && (
            <Sheet
              backgroundColor="#FFFDF0"
              lineColor="#000000"
              lineOpacity={0.2}
              highlightOpacity={0.1}
              yaw={2.35}
              geometryScale={15}
              tilt={0.5}
              resolution={300}
              gridSize={20}
              waveAmplitude={0.8}
              waveFrequency={0.8}
              waveSpeed={0.5}
            />
          )}
        </div>
        {/* Gradient fade overlay - bg color at top, transparent at bottom */}
        <div
          className="absolute inset-x-0 top-0 h-24 pointer-events-none z-10"
          style={{
            background:
              "linear-gradient(to bottom, #FFFDF0 0%, transparent 100%)",
          }}
        />
      </div>
    </section>
  );
}

interface ApproachCard {
  title: string;
  body: string;
}

const approachCards: ApproachCard[] = [
  {
    title: "Semantics",
    body: "Our models integrate formal logic and semantics at the architectural level, providing provable invariants of model behavior under all conditions.",
  },
  {
    title: "Reasoning",
    body: "Unlike large language models, our category-theoretic architectures embed reasoning into the architecture itself. Program execution, search, and synthesis all occur in the same architecture, fundamentally blurring the line between neural, symbolic, and reasoning.",
  },
  {
    title: "Categories",
    body: "By leveraging category theory to inform the construction of our architectures we are able to seamlessly unify discrete concepts like types and programs with fuzzy concepts like vector spaces and optimization with a degree of sophistication inaccessible to other architectures.",
  },
];

function OurThesisSection() {
  // Key to force remount visualizations when tab becomes visible again
  const [vizKey, setVizKey] = useState(0);
  // Track if section is in viewport to lazy load visualizations
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setVizKey((prev) => prev + 1);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Intersection observer to mount/unmount visualizations based on viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry?.isIntersecting ?? false);
      },
      { rootMargin: "100px" } // Start loading slightly before section enters viewport
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="our-approach"
      className="relative bg-[#FFFDF0] scroll-mt-20 overflow-hidden"
    >
      <div className="relative z-10 mx-auto max-w-[96rem] px-8 pt-30 md:pt-40 pb-20 md:pb-40 lg:px-24">
        <ContentLockup
          label="Our Thesis"
          title="Applied Category Theory"
          description="Symbolica is a pioneering AI research lab founded in 2022. We are a team of software engineers and mathematicians leveraging a form of abstract mathematics, known as category theory, to deliver exceptional breakthroughs in modern artificial intelligence research."
          light
        />

        {/* Static Cards Grid - with gaps and borders */}
        <div
          ref={cardsRef}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {approachCards.map((card, index) => (
            <div
              key={index}
              className="relative flex flex-col p-8 min-h-[480px] sm:min-h-[540px] border border-black/10 bg-[#FFFDF0] overflow-hidden"
            >
              {/* Parametric visualization container - fixed position in card */}
              <div
                className="relative w-full h-[260px] -mx-8 -mt-8"
                style={{ width: "calc(100% + 4rem)" }}
              >
                {/* Visualization fills container - staggered fade-in: 1s, 1.5s, 2s */}
                <div className="absolute inset-0">
                  {/* Rotated right: Card 2 viz → Card 0, Card 0 viz → Card 1, Card 1 viz → Card 2 */}
                  {index === 0 && (
                    <img
                      src="/images/landing/symbolica-0-shapes.png"
                      alt="Shapes visualization"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 1 && (
                    <img
                      src="/images/landing/symbolica-1-arcs-lines-dots.png"
                      alt="Blooming network complex visualization"
                      className="w-full h-full object-cover"
                    />
                  )}
                  {index === 2 && (
                    <img
                      src="/images/landing/symbolica-3-commutative-network.png"
                      alt="Commutative network visualization"
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                {/* Gradient fade at bottom - positioned relative to visualization container */}
                <div
                  className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none z-10"
                  style={{
                    background:
                      "linear-gradient(to bottom, transparent 0%, #FFFDF0 100%)",
                  }}
                />
              </div>

              {/* Content at bottom */}
              <div className="mt-auto">
                <h3 className="font-arizona-mix text-[22px] leading-[1.3] font-medium text-[#0a0a0a] tracking-[-0.5px] text-pretty">
                  {card.title}
                </h3>
                <p className="font-arizona-sans text-[16px] text-[#0a0a0a] mt-4 leading-relaxed">
                  {card.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AgenticaSection() {
  const searchParams = useSearchParams();
  const isFullWidth = searchParams.get("fullwidthproduct") === "1";

  if (isFullWidth) {
    return (
      <section className="relative bg-[#F5F3E7]">
        {/* Full-width dark container */}
        <div className="relative bg-[#0f0f0f] overflow-hidden">
          {/* Content area */}
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-12 px-8 lg:px-24 py-32 max-w-[96rem] mx-auto">
            {/* Animation / Illustration */}
            <div className="flex-1 flex items-center justify-center min-h-[400px]"></div>

            {/* Lockup content */}
            <div className="flex-1">
              <ContentLockup
                label="Product"
                title="Introducing Agentica"
                subhead="Agentica provides agents with the ability to iteratively decompose, investigate, and dynamically solve problems by equipping them with the single most expressive mechanism: arbitrary code execution."
                description="Leveraging types and composition, two of the fundamental building blocks of category theory, we enable users to build multi-agent systems that deliver state of the art performance on top of any model."
              />
              {/* CTA */}
              <div className="mt-8">
                <a href={env.NEXT_PUBLIC_AGENTICA_BASE_URL}>
                  <PillButton variant="secondary">Try Agentica</PillButton>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default: Boxed style with full-width darker bg wrapper and borders
  return (
    <section className="relative bg-[#F0EDE0] border-t border-b border-black/10 py-28 lg:py-36 overflow-x-clip">
      {/* Top line - 16px above the black box, extends full width */}
      <div
        className="absolute left-1/2 h-px bg-black/10 top-24 lg:top-32"
        style={{ width: "10000px", transform: "translateX(-5000px)" }}
      />
      {/* Bottom line - 16px below the black box, extends full width */}
      <div
        className="absolute left-1/2 h-px bg-black/10 bottom-24 lg:bottom-32"
        style={{ width: "10000px", transform: "translateX(-5000px)" }}
      />
      <div className="relative mx-auto max-w-[110rem] px-4 lg:px-10">
        {/* Left vertical line - 16px from black box edge */}
        <div className="absolute w-px bg-black/10 left-0 lg:left-6 -top-28 -bottom-28 lg:-top-36 lg:-bottom-36 hidden sm:block" />
        {/* Right vertical line - 16px from black box edge */}
        <div className="absolute w-px bg-black/10 right-0 lg:right-6 -top-28 -bottom-28 lg:-top-36 lg:-bottom-36 hidden sm:block" />
        {/* Dark container - square edges */}
        <div className="relative bg-[#0f0f0f] overflow-visible px-8 lg:px-24 pt-20 pb-24">
          {/* Centered header */}
          <div className="text-center max-w-6xl mx-auto">
            <div className="font-ibm-plex-mono text-[13px] font-medium uppercase tracking-[0.2em] text-white/60 mb-4 text-left lg:text-center">
              PRODUCT
            </div>
            <h2 className="font-arizona-mix text-[32px] md:text-[42px] lg:text-[52px] leading-[1.1] font-light text-white tracking-[-1px] text-pretty text-left lg:text-center">
              Introducing Agentica
            </h2>
          </div>

          {/* Two-column content area - THIS ONE */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 max-w-[96rem] mx-auto">
            {/* Illustration */}
            <div className="flex-1 flex items-center justify-center">
              <img
                src="/images/agentica/feature-4.png"
                alt="Agentica feature"
                className="w-full max-w-md rounded-xl brightness-[0.85]"
              />
            </div>

            {/* Description and CTA */}
            <div className="flex-1 flex flex-col justify-start lg:justify-center w-full">
              <ContentLockup
                subhead="Agentica provides agents with the ability to iteratively decompose, investigate, and dynamically solve problems by equipping them with the single most expressive mechanism: arbitrary code execution."
                description="Leveraging types and composition, two of the fundamental building blocks of category theory, we enable users to build multi-agent systems that deliver state of the art performance on top of any model."
              />
              {/* CTAs */}
              <div className="mt-8 flex flex-row flex-wrap items-start gap-3">
                <a href={env.NEXT_PUBLIC_AGENTICA_BASE_URL}>
                  <PillButton variant="secondary">Try Agentica</PillButton>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OurTeamSection() {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection observer to mount/unmount visualization based on viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry?.isIntersecting ?? false);
      },
      { rootMargin: "100px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative bg-[#FFFDF0] mb-[-6rem] md:mb-[-9rem] overflow-hidden">
      <div className="relative z-10 mx-auto max-w-[96rem] px-8 pt-32 pb-40 md:pt-40 md:pb-48 lg:px-24">
        <div className="flex flex-col lg:flex-row lg:items-stretch lg:justify-between">
          {/* Left side - Text Content with blurred background */}
          <div className="relative flex-1 lg:pr-16">
            {/* Blurred background overlay for text area */}
            <div className="absolute inset-0 -left-24 -top-32 -bottom-40 md:-top-40 md:-bottom-48 backdrop-blur-md bg-[#FFFDF0]/5 2xl:border-l 2xl:border-black/10" />

            {/* Vertical separator line on the right */}
            <div
              className="hidden lg:block absolute right-0 top-0 bottom-0 w-px bg-black/10"
              style={{ top: "-10rem", bottom: "-12rem" }}
            />

            {/* Content */}
            <div className="relative z-10">
              <ContentLockup
                label="San Francisco & London"
                title="Careers"
                subhead="Symbolica is a world-class team of researchers and engineers, with offices in San Francisco and London."
                description="We are united by deep experience in building mathematically grounded AI systems, bringing experience from leading institutions and companies including Tesla AI, Meta, HFT Funds, Snap Research, TikTok, Wolfram, Imperial College London, Cambridge, and Oxford."
                light
              />
              {/* CTA */}
              <div className="mt-10">
                <Link href="/careers">
                  <PillButton variant="outline">Work With Us</PillButton>
                </Link>
              </div>
            </div>
          </div>

          {/* Right side spacer */}
          <div className="hidden lg:block w-[520px] flex-shrink-0" />
        </div>
      </div>
    </section>
  );
}
